// P7 · Reframe / What-if explorer (exploratory C5). Moves the framing toward a target
// (named pole/outlet preset = instant precomputed; custom slider mix = live /api/reframe
// with simulation fallback) and shows the text, the requested-vs-achieved radar, and the
// grounded change log. Facts fixed · framing variable. No publish/export (spec §4a.8).

import { useRef, type CSSProperties, type Dispatch } from "react";
import type { Bundle, ReframeResult } from "../types";
import type { UIState, Patch } from "../state";
import type { Telemetry } from "../telemetry";
import { INK, sectionLabel } from "../style";
import { buildCacheKey, requestReframe } from "../reframe";

const DIR_LABEL: Record<string, string> = {
  as_read: "as published", neutral: "toward neutral", pro_institutional: "toward institutional",
  pro_civilian: "toward civilian", mixed: "mixed", custom: "custom",
};

export function Reframe({ b, s, set, tel, active, reframed, isAsRead, invariantOk, useStacked }: {
  b: Bundle; s: UIState; set: Dispatch<Patch>; tel: Telemetry;
  active: ReframeResult; reframed: boolean; isAsRead: boolean; invariantOk: boolean; useStacked: boolean;
}) {
  const RF = b.reframe;
  const liveTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const LIVE_MAX = 5;

  const sliderTargets = s.sliderTargets || {};
  const structTargets = s.structTargets || {};
  const invariantBad = !invariantOk;

  // ---- live custom-mix path (gated backend + simulation fallback) ----
  const runLiveReframe = async () => {
    if (s.liveCalls >= RF.live.session_call_cap || s.liveLoading) return;
    const key = buildCacheKey(RF, sliderTargets, structTargets);
    tel.log("reframe_request", { reframe_cache_key: key, targets: { ...sliderTargets }, structural_targets: { ...structTargets }, cache: "miss" });
    const callsNow = s.liveCalls + 1;
    set({ liveLoading: true, liveIter: 1, liveCalls: callsNow });

    if (liveTimer.current) clearInterval(liveTimer.current);
    liveTimer.current = setInterval(() => {
      set({ liveIter: Math.min(LIVE_MAX, (s.liveIter || 1) + 1) });
    }, 700);

    const outcome = await requestReframe(
      RF,
      { session_id: tel.session_id, participant_id: tel.participant_id, join_gva_id: b.incident.join_gva_id, target_outlet: b.target.outlet },
      sliderTargets, structTargets
    );
    if (liveTimer.current) { clearInterval(liveTimer.current); liveTimer.current = null; }

    const res = outcome.result;
    if (outcome.source === "cache") tel.log("reframe_cache_hit", { reframe_cache_key: key });
    if (res.invariant && res.invariant.ok === false) {
      tel.log("reframe_invariant_block", { reframe_cache_key: key, reason: !res.invariant.quotes_preserved ? "quotes" : res.invariant.facts_added?.length ? "facts_added" : "facts_dropped" });
    } else {
      tel.log("reframe_apply", { profile_id: "__custom", reframe_cache_key: key, source: outcome.source === "live" ? "live" : "live", requested_target: res.requested_target, achieved_scores: res.achieved_scores, residuals: res.residuals, manipulation_check: res.manipulation_check });
    }
    if (callsNow >= RF.live.session_call_cap) tel.log("reframe_budget_exhausted", { session_call_cap: RF.live.session_call_cap });
    set({ liveLoading: false, customActive: true, liveResult: res, readView: "original" });
  };

  const onPreset = (pid: string) => {
    const pc = RF.precomputed[pid];
    if (!pc) return;
    tel.log("reframe_apply", { profile_id: pid, source: "precomputed", manipulation_check: pc.manipulation_check, requested_target: pc.requested_target, achieved_scores: pc.achieved_scores, residuals: pc.residuals });
    // surface the trace on the reading pane (flip the doc-view tab back from "edit")
    set({ reframeProfile: pid, readView: "original", customActive: false, liveResult: null, reframeChangeOpen: null, sliderTargets: { ...pc.requested_target }, structTargets: { ...pc.structural_targets } });
  };

  // ---- preset chips ----
  const presetChips = RF.profiles.map((p) => {
    const on = !s.customActive && s.reframeProfile === p.profile_id;
    const n = p.n_articles ?? 0;
    const prov = p.kind === "outlet" ? `outlet · n=${n}` : p.kind === "pole" ? (n > 0 ? `pole · n=${n}` : "pole") : "as published";
    return { pid: p.profile_id, display: p.display, prov, on };
  });

  // ---- radar geometry ----
  const RA = RF.axes;
  const cx = 120, cy = 120, Rr = 84;
  const normR = (axis: string, v: number) => { const sc = RF.axis_scales[axis] || [-2, 2]; return Math.max(0, Math.min(1, (v - sc[0]) / (sc[1] - sc[0]))); };
  const ptOf = (axis: string, v: number, i: number): [number, number] => { const ang = -Math.PI / 2 + i * (Math.PI / 4); const rr = normR(axis, v) * Rr; return [cx + rr * Math.cos(ang), cy + rr * Math.sin(ang)]; };
  const ringPts = (frac: number) => RA.map((ax, i) => { const ang = -Math.PI / 2 + i * (Math.PI / 4); return (cx + frac * Rr * Math.cos(ang)) + "," + (cy + frac * Rr * Math.sin(ang)); }).join(" ");
  let reqPts = "", achPts = "";
  const spokes = RA.map((ax, i) => {
    const ang = -Math.PI / 2 + i * (Math.PI / 4);
    const ex = cx + Rr * Math.cos(ang), ey = cy + Rr * Math.sin(ang);
    const [rqx, rqy] = ptOf(ax, Number(active.requested_target[ax]) || 0, i);
    const [acx, acy] = ptOf(ax, Number(active.achieved_scores[ax]) || 0, i);
    reqPts += rqx.toFixed(1) + "," + rqy.toFixed(1) + " ";
    achPts += acx.toFixed(1) + "," + acy.toFixed(1) + " ";
    const lr = Rr + 13, lx = cx + lr * Math.cos(ang), ly = cy + lr * Math.sin(ang);
    const resid = Math.abs(active.residuals[ax] || 0);
    const big = resid >= 1, cosa = Math.cos(ang);
    return {
      axis: ax, ex: ex.toFixed(1), ey: ey.toFixed(1), lx: lx.toFixed(1), ly: ly.toFixed(1),
      anchor: cosa > 0.3 ? "start" : cosa < -0.3 ? "end" : "middle",
      dy: Math.sin(ang) > 0.3 ? "9" : Math.sin(ang) < -0.3 ? "-2" : "3",
      short: (RF.axis_labels[ax] || ax).split(" ")[0],
      labelFill: big ? "#b3261e" : "rgba(55,53,47,.5)", labelWeight: big ? 700 : 600,
      dotX: acx.toFixed(1), dotY: acy.toFixed(1), dotFill: big ? "#b3261e" : INK,
    };
  });
  const resistedAxes = RA.filter((ax) => Math.abs(active.residuals[ax] || 0) >= 1).map((ax) => RF.axis_labels[ax] || ax);
  const hasResisted = reframed && resistedAxes.length > 0;

  // ---- manipulation check ----
  const mc = active.manipulation_check || ({} as any);
  const mcChipLabel = isAsRead ? "As published" : mc.landed ? "Reframe landed" : "Partial reframe";
  const mcChipStyle: CSSProperties = {
    fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 4, letterSpacing: ".02em",
    background: isAsRead ? "#f1f0ec" : mc.landed ? INK : "#fdf0e9",
    color: isAsRead ? "rgba(55,53,47,.6)" : mc.landed ? "#fff" : "#a4622a",
    border: mc.landed || isAsRead ? "none" : "1px solid #f0d6b4",
  };

  // The reframe diff is now rendered as tracked changes over the reading pane (C5); the
  // panel keeps only the requested-vs-achieved radar, the change log, and the controls.
  const reframeChanges = reframed ? (active.change_log || []) : [];
  const budgetLeft = RF.live.session_call_cap - s.liveCalls;
  const budgetExhausted = budgetLeft <= 0;
  const customNote = s.customActive && active._custom
    ? `Simulated live result, closest reachable framing: ${active._closest}. The honest gap shows on the radar.` : "";

  return (
    <section style={{ marginTop: 20, background: "#fff", border: "1px solid rgba(55,53,47,.12)", borderRadius: 6, overflow: "hidden" }}>
      {/* header */}
      <div style={{ padding: "14px 22px", background: "#f7f6f3", borderBottom: "1px solid rgba(55,53,47,.09)", display: "flex", alignItems: "center", gap: 11 }}>
        <span style={{ width: 24, height: 24, borderRadius: 5, background: INK, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flex: "none" }}>⚗</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>Reframe this story</span>
            <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "rgba(55,53,47,.5)", background: "#e7e6e1", padding: "2px 6px", borderRadius: 4 }}>Exploratory · C5</span>
          </div>
          <div style={{ fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>Move the framing toward a target and watch the same facts get retold.</div>
        </div>
      </div>

      {/* honesty banner */}
      <div style={{ display: "flex", gap: 9, alignItems: "center", padding: "10px 22px", background: "#fbfbfa", borderBottom: "1px solid rgba(55,53,47,.08)" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: INK, letterSpacing: ".02em" }}>Facts fixed</span>
        <span style={{ width: 1, height: 13, background: "rgba(55,53,47,.18)" }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(55,53,47,.5)", letterSpacing: ".02em" }}>Framing variable</span>
        <span style={{ fontSize: 11, color: "rgba(55,53,47,.5)" }}>The facts stay fixed; only the wording moves. Every edit is grounded in the change log below.</span>
      </div>

      <div style={{ padding: "18px 22px 22px" }}>
        {/* preset picker */}
        <div style={{ ...sectionLabel, marginBottom: 8 }}>Target framing</div>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
          {presetChips.map((p) => (
            <button key={p.pid} onClick={() => onPreset(p.pid)}
              style={{ font: "inherit", textAlign: "left", cursor: "pointer", borderRadius: 7, padding: "8px 11px", display: "flex", flexDirection: "column", gap: 0, border: "1px solid " + (p.on ? INK : "rgba(55,53,47,.16)"), background: p.on ? INK : "#fff", color: p.on ? "#fff" : INK, transition: "all .12s" }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{p.display}</span>
              <span style={{ fontSize: 9.5, fontWeight: 500, color: p.on ? "rgba(255,255,255,.7)" : "rgba(55,53,47,.4)", marginTop: 1 }}>{p.prov}</span>
            </button>
          ))}
        </div>

        {/* advanced disclosure */}
        <button onClick={() => set({ advancedOpen: !s.advancedOpen })} style={{ display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "4px 0", fontSize: 12, fontWeight: 600, color: "rgba(55,53,47,.6)" }}>
          <span style={{ display: "inline-block", fontSize: 10, color: "rgba(55,53,47,.4)", transform: s.advancedOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span> Advanced: build a custom target by axis
        </button>
        {s.advancedOpen && (
          <div className="lensfade" style={{ marginTop: 10, background: "#fbfbfa", border: "1px solid rgba(55,53,47,.1)", borderRadius: 7, padding: "14px 15px" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 13, padding: "9px 11px", background: "#fff", border: "1px dashed rgba(55,53,47,.25)", borderRadius: 5 }}>
              <span style={{ flex: "none", fontSize: 12 }}>⚠</span>
              <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "rgba(55,53,47,.65)" }}>The axes are correlated, so a hand-built combination may be unreachable. The system shows the requested-vs-achieved gap rather than faking a match. Presets carry the real correlations.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "9px 18px", marginBottom: 13 }}>
              {RA.map((ax) => {
                const sc = RF.axis_scales[ax] || [-2, 2];
                const val = sliderTargets[ax] ?? 0;
                return (
                  <div key={ax}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                      <span style={{ fontSize: 11, color: "rgba(55,53,47,.62)" }}>{RF.axis_labels[ax] || ax}</span>
                      <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 10.5, fontWeight: 600, color: INK }}>{val > 0 ? "+" : ""}{val}</span>
                    </div>
                    <input type="range" min={sc[0]} max={sc[1]} step={1} value={val}
                      onChange={(e) => { tel.log("reframe_slider_change", { axis: ax, value: parseInt(e.target.value, 10) }); set({ sliderTargets: { ...sliderTargets, [ax]: parseInt(e.target.value, 10) } }); }}
                      style={{ width: "100%", accentColor: INK, height: 3 }} />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {Object.entries(RF.structural).map(([key, def]) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: "rgba(55,53,47,.62)", width: 96, flex: "none" }}>{def.label}</span>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {def.options.map((o) => {
                      const on = structTargets[key] === o;
                      return (
                        <button key={o} onClick={() => set({ structTargets: { ...structTargets, [key]: o } })}
                          style={{ font: "inherit", fontSize: 11, fontWeight: 600, padding: "4px 8px", borderRadius: 5, cursor: "pointer", border: "1px solid " + (on ? INK : "rgba(55,53,47,.16)"), background: on ? INK : "#fff", color: on ? "#fff" : "rgba(55,53,47,.6)" }}>
                          {def.display[o]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <button onClick={runLiveReframe} disabled={budgetExhausted}
                style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "8px 14px", borderRadius: 6, cursor: budgetExhausted ? "not-allowed" : "pointer", border: "none", background: budgetExhausted ? "rgba(55,53,47,.18)" : INK, color: "#fff", opacity: budgetExhausted ? 0.7 : 1 }}>
                Apply custom mix
              </button>
              <span style={{ fontSize: 11, color: "rgba(55,53,47,.5)" }}>{budgetExhausted ? "Live budget reached; presets still work." : `${budgetLeft} live reframes left this session`}</span>
            </div>
          </div>
        )}

        {/* invariant guard */}
        {invariantBad && (
          <div style={{ marginTop: 16, padding: 16, background: "#fff", border: "1px solid rgba(179,38,30,.4)", borderRadius: 7, textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#b3261e", marginBottom: 3 }}>Couldn't reframe without changing the facts</div>
            <div style={{ fontSize: 12, color: "rgba(55,53,47,.6)" }}>This target would require dropping or inventing a fact, so it isn't shown. The facts are the boundary.</div>
          </div>
        )}

        {/* live loading */}
        {s.liveLoading && (
          <div style={{ marginTop: 16, padding: 16, background: "#fbfbfa", border: "1px solid rgba(55,53,47,.1)", borderRadius: 7 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>Reframing… writer + judge loop</span>
              <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 11, color: "rgba(55,53,47,.55)" }}>iter {s.liveIter}/{LIVE_MAX}</span>
            </div>
            <div style={{ height: 5, background: "rgba(55,53,47,.1)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: Math.min(100, (s.liveIter / LIVE_MAX) * 100) + "%", background: INK, borderRadius: 3, transition: "width .3s" }} />
            </div>
          </div>
        )}

        {/* result */}
        {!invariantBad && !s.liveLoading && (
          <div style={{ marginTop: 18, borderTop: "1px solid rgba(55,53,47,.09)", paddingTop: 16 }}>
            {customNote && (
              <div style={{ marginBottom: 13, padding: "8px 11px", background: "#fbfbfa", border: "1px solid rgba(55,53,47,.12)", borderRadius: 5, fontSize: 11.5, color: "rgba(55,53,47,.62)" }}>{customNote}</div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={mcChipStyle}>{mcChipLabel}</span>
              <span style={{ fontSize: 12, color: "rgba(55,53,47,.6)" }}>{mc.axes_on_target}/{mc.axes_total} axes on target · shift {DIR_LABEL[mc.achieved_direction] || mc.achieved_direction}</span>
            </div>

            <div>
              {/* radar */}
              <div>
                <div style={{ ...sectionLabel, marginBottom: 6 }}>Requested vs achieved</div>
                <svg viewBox="0 0 240 240" style={{ width: "100%", maxWidth: 280, display: "block" }}>
                  <polygon points={ringPts(1)} fill="none" stroke="rgba(55,53,47,.12)" strokeWidth="1" />
                  <polygon points={ringPts(0.5)} fill="none" stroke="rgba(55,53,47,.08)" strokeWidth="1" />
                  {spokes.map((sp) => <line key={"l" + sp.axis} x1={cx} y1={cy} x2={sp.ex} y2={sp.ey} stroke="rgba(55,53,47,.1)" strokeWidth="1" />)}
                  <polygon points={reqPts.trim()} fill="rgba(55,53,47,.06)" stroke="rgba(55,53,47,.45)" strokeWidth="1.5" strokeDasharray="3 2" />
                  <polygon points={achPts.trim()} fill="rgba(179,38,30,.10)" stroke={INK} strokeWidth="1.5" />
                  {spokes.map((sp) => <circle key={"c" + sp.axis} cx={sp.dotX} cy={sp.dotY} r="2.4" fill={sp.dotFill} />)}
                  {spokes.map((sp) => (
                    <text key={"t" + sp.axis} x={sp.lx} y={sp.ly} dy={sp.dy} textAnchor={sp.anchor as any} fontFamily="'Source Sans 3',sans-serif" fontSize="8.5" fontWeight={sp.labelWeight} fill={sp.labelFill}
                      onClick={() => { tel.log("reframe_radar_spoke_tap", { axis: sp.axis, residual: active.residuals[sp.axis] || 0 }); set({ radarSpoke: sp.axis }); }} style={{ cursor: "pointer" }}>
                      {sp.short}
                    </text>
                  ))}
                </svg>
                <div style={{ display: "flex", gap: 14, marginTop: 4, fontSize: 10.5, color: "rgba(55,53,47,.55)" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 0, borderTop: "2px dashed rgba(55,53,47,.5)" }} />Requested</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 0, borderTop: "2px solid " + INK }} />Achieved</span>
                </div>
                {hasResisted && (
                  <p style={{ margin: "8px 0 0", fontSize: 11, lineHeight: 1.45, color: "#9a3b34" }}><span style={{ fontWeight: 600 }}>Framing resisted:</span> {resistedAxes.join(", ")}: no single span carries these, so the writer can't move them on demand.</p>
                )}
              </div>

              {/* where the edits show */}
              <div style={{ marginTop: 14, padding: "9px 11px", background: "#fbfbfa", border: "1px solid rgba(55,53,47,.1)", borderRadius: 6, fontSize: 11.5, lineHeight: 1.5, color: "rgba(55,53,47,.62)" }}>
                {!reframed
                  ? "This is the article exactly as published. Pick a framing above; the same facts get retold, and the edits track directly over the article."
                  : "The edits are tracked directly over the article: additions underlined in green, removals struck through."}
              </div>
            </div>

            {/* change log */}
            {reframed && reframeChanges.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ ...sectionLabel, marginBottom: 8 }}>Why each change, grounded in the fact table</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {reframeChanges.map((c, i) => {
                    const open = s.reframeChangeOpen === i;
                    const claims = (c.fact_claim_ids || []).join(", ");
                    return (
                      <button key={i} onClick={() => set({ reframeChangeOpen: open ? null : i })}
                        style={{ background: "#fff", border: "1px solid " + (open ? "rgba(55,53,47,.45)" : "rgba(55,53,47,.12)"), borderRadius: 7, padding: "11px 12px", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit", transition: "border-color .12s" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                          <span style={{ display: "inline-block", fontSize: 10, color: "rgba(55,53,47,.4)", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>
                          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: INK, background: "#f1f0ec", padding: "2px 7px", borderRadius: 4 }}>{c.axis}</span>
                          <span style={{ flex: 1 }} />
                          {claims && <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 10, color: "rgba(55,53,47,.5)" }}>{claims}</span>}
                        </div>
                        {open && (
                          <div className="lensfade" style={{ marginTop: 10, paddingLeft: 19 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
                              <div><div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", color: "rgba(55,53,47,.45)", fontWeight: 600, marginBottom: 2 }}>Before</div><div style={{ fontSize: 12.5, lineHeight: 1.45, color: "rgba(55,53,47,.6)" }}>{c.before}</div></div>
                              <div><div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", color: INK, fontWeight: 700, marginBottom: 2 }}>After</div><div style={{ fontSize: 12.5, lineHeight: 1.45, color: INK }}>{c.after}</div></div>
                            </div>
                            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "rgba(55,53,47,.7)" }}><span style={{ fontWeight: 700, color: INK }}>Why:</span> {c.why}</p>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* no-publish footer */}
            <div style={{ marginTop: 16, padding: "10px 13px", background: "#fbfbfa", border: "1px solid rgba(55,53,47,.1)", borderRadius: 6, display: "flex", gap: 9, alignItems: "flex-start" }}>
              <span style={{ flex: "none", fontSize: 12, color: "rgba(55,53,47,.5)" }}>⚠</span>
              <p style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: "rgba(55,53,47,.58)" }}>Reframed text is AI-generated study material, shown only beside the fixed facts and the change log. There is no publish or export path; it never leaves the study session.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
